<script lang="ts">
  import {onDestroy, createEventDispatcher} from 'svelte';
  import {uid} from 'uid';
  import {type MaskedInputProps} from '.';
  import {BaseInput} from 'Catalyst/components/BaseInput';
  import {imask} from '@imask/svelte';
  import IMask from 'imask';
  import type {FactoryArg, InputMask} from 'imask';

  // eslint-disable-next-line no-unused-vars
  type $$Props = MaskedInputProps;

  export let id: MaskedInputProps['id'] = uid();
  export let inputTestId: MaskedInputProps['inputTestId'] = `${id}-masked-input`;
  export let labelTestId: MaskedInputProps['labelTestId'] =
    'masked-input-label';
  export let value: MaskedInputProps['value'] = '';
  export let unmask: MaskedInputProps['unmask'] = false;
  export let iMaskOptions: MaskedInputProps['iMaskOptions'];
  export let caretPosition: MaskedInputProps['caretPosition'] = 1;
  export let placeholder: MaskedInputProps['placeholder'] = '';
  export let ref: MaskedInputProps['ref'] = null;
  export let showPlaceholder: MaskedInputProps['showPlaceholder'] = false;
  export let name: MaskedInputProps['name'] = '';
  export let size: MaskedInputProps['size'] = undefined;
  export let style: MaskedInputProps['style'] = undefined;

  let iMaskRef: InputMask<FactoryArg>;
  let maskedValue: MaskedInputProps['value'] = '';

  const dispatch = createEventDispatcher();

  $: {
    if (ref && iMaskOptions) {
      if (!iMaskRef) {
        iMaskRef = IMask(ref, iMaskOptions);
        iMaskRef.on('accept', () => {
          value = getValue();
          maskedValue = iMaskRef.value;
          dispatch('change', value);
          caretPosition = iMaskRef.el.selectionStart;
        });
        iMaskRef.on('complete', () => {
          value = getValue();
          maskedValue = iMaskRef.value;
        });
      } else {
        // Update options if they change but preserve caret position instead of moving to the end of the input
        const caretPosition = iMaskRef.el.selectionStart;
        iMaskRef.updateOptions(iMaskOptions);
        ref.setSelectionRange(caretPosition, caretPosition);
      }
      // Sync value whenever it changes externally
      if (getValue() !== value) {
        writeValue(value);
      }
    }
  }

  const getValue = (): any => {
    if (unmask === 'typed') return iMaskRef.typedValue;
    if (unmask) return iMaskRef.unmaskedValue;
    return iMaskRef.value;
  };

  const setValue = (v: any): void => {
    v = v === null || v === undefined ? '' : v;
    if (unmask === 'typed') iMaskRef.typedValue = v;
    else if (unmask) iMaskRef.unmaskedValue = v;
    else iMaskRef.value = v;
  };

  const writeValue = (v: any): void => {
    if (
      getValue() !== v ||
      // handle cases like Number('') === 0,
      // for details see https://github.com/uNmAnNeR/imaskjs/issues/134
      (typeof v !== 'string' && value === '' && !iMaskRef.el.isActive)
    ) {
      setValue(v);
    }
  };

  $: floating =
    !!value || ('lazy' in iMaskOptions && iMaskOptions.lazy === false);

  onDestroy(() => {
    if (iMaskRef) iMaskRef.destroy();
    iMaskRef = undefined;
  });
  const onFocus = (e: FocusEvent) => {
    showPlaceholder = true;
    dispatch('focus', e);
  };
</script>

<BaseInput
  {...$$restProps}
  {floating}
  {id}
  {inputTestId}
  {labelTestId}
  placeholder={showPlaceholder ? placeholder : ''}
  bind:ref
  action={node => imask(node, iMaskRef)}
  on:blur
  on:click
  on:input
  on:focus={onFocus}
  {name}
  value={maskedValue}
  {size}
  {style}
>
  <input
    slot="hidden-input"
    id={`${id}-hidden-input`}
    data-testid={`${inputTestId}-hidden`}
    {name}
    type="hidden"
    bind:value
  />
  <slot name="prefix" slot="prefix" />
  <slot name="suffix" slot="suffix" />
</BaseInput>
